import type {
  FaceMeasurementMode,
  FaceMeasurementRecord,
  FaceMeasurementRecordSource,
  PdMeasurementRecord,
  PdMeasurementRecordSource,
} from './types';
import type { PDCardCalibrationMeasurementResult } from '@zeelool/pd-card-calibration';

export const FACE_WIDTH_RANGE_MM = { min: 110, max: 180 } as const;
export const PRODUCT_PD_RANGE_MM = { min: 54, max: 78 } as const;

export interface RawFaceMeasurementResult {
  mode: FaceMeasurementMode;
  faceWidthMm: number;
  confidencePercent: number;
  onlinePdMm?: number;
}

export type FaceMeasurementResultResolution =
  | { status: 'valid'; record: FaceMeasurementRecord }
  | { status: 'invalid' };

export type PdMeasurementResultResolution =
  | { status: 'valid'; record: PdMeasurementRecord }
  | { status: 'invalid' };

export function toRawFaceMeasurementResult(
  result: PDCardCalibrationMeasurementResult,
): RawFaceMeasurementResult {
  if (result.mode === 'card') {
    return {
      mode: result.mode,
      faceWidthMm: result.faceWidthMm,
      onlinePdMm: result.totalMm,
      confidencePercent: result.confidence.percent,
    };
  }

  return {
    mode: result.mode,
    faceWidthMm: result.faceWidthMm,
    confidencePercent: result.confidence.percent,
  };
}

function roundToStep(value: number, step: number) {
  return Math.round(value / step) * step;
}

function isWithin(value: number, range: { min: number; max: number }) {
  return Number.isFinite(value) && value >= range.min && value <= range.max;
}

export function resolveRecommendedFrameSizes(faceWidthMm: number): string[] {
  if (!isWithin(faceWidthMm, FACE_WIDTH_RANGE_MM)) return [];
  if (faceWidthMm <= 124) return ['narrow'];
  if (faceWidthMm <= 128) return ['narrow', 'medium'];
  if (faceWidthMm <= 132) return ['medium', 'wide'];
  if (faceWidthMm <= 136) return ['wide', 'extra wide'];
  return ['extra wide'];
}

export function resolveFaceMeasurementResult({
  raw,
  entrySource,
  sdkVersion,
  now = Date.now(),
}: {
  raw: RawFaceMeasurementResult;
  entrySource: FaceMeasurementRecordSource;
  sdkVersion: string;
  now?: number;
}): FaceMeasurementResultResolution {
  if (!isWithin(raw.faceWidthMm, FACE_WIDTH_RANGE_MM)) return { status: 'invalid' };

  const faceWidthMm = roundToStep(raw.faceWidthMm, 0.1);

  return {
    status: 'valid',
    record: {
      recordId: `${now}-${entrySource}`,
      measuredAt: new Date(now).toISOString(),
      entrySource,
      sourceMode: raw.mode,
      faceWidthMm,
      recommendedFrameSizes: resolveRecommendedFrameSizes(faceWidthMm),
      sdkVersion,
      confidencePercent: Math.min(100, Math.max(0, Math.round(raw.confidencePercent))),
    },
  };
}

export function resolvePdMeasurementResult({
  raw,
  entrySource,
  sdkVersion,
  now = Date.now(),
}: {
  raw: RawFaceMeasurementResult;
  entrySource: PdMeasurementRecordSource;
  sdkVersion: string;
  now?: number;
}): PdMeasurementResultResolution {
  const onlinePdMm = raw.onlinePdMm;
  if (
    raw.mode !== 'card' ||
    onlinePdMm === undefined ||
    !isWithin(onlinePdMm, PRODUCT_PD_RANGE_MM)
  ) {
    return { status: 'invalid' };
  }

  return {
    status: 'valid',
    record: {
      recordId: `${now}-${entrySource}`,
      measuredAt: new Date(now).toISOString(),
      entrySource,
      sourceMode: 'card',
      onlinePdMm: roundToStep(onlinePdMm, 0.5),
      sdkVersion,
      confidencePercent: Math.min(100, Math.max(0, Math.round(raw.confidencePercent))),
    },
  };
}
